import { useEffect, useRef, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import api from '../../services/api';

export interface Beneficiary {
  id: string;
  name: string;
  email: string;
  username: string;
  employeeId: string;
  designation: string;
  department: string;
}

interface Props {
  value: Beneficiary | null;
  onSelect: (b: Beneficiary | null) => void;
}

const fc = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-600 transition-all bg-white';

export default function BeneficiaryPicker({ value, onSelect }: Props) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState<Beneficiary[]>([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Debounced search against the Employee Master lookup.
  useEffect(() => {
    if (value) return;                       // a selection is shown; don't search
    const q = query.trim();
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/tickets/beneficiaries', { params: { search: q || undefined } });
        if (active) { setResults(res.data.beneficiaries ?? []); setOpen(true); }
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [query, value]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (value) {
    return (
      <div className="flex items-start justify-between gap-3 rounded-lg border border-violet-200 bg-violet-50/60 p-3">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-white">
            <Check size={13} />
          </span>
          <div className="text-sm">
            <div className="font-semibold text-navy">{value.name}</div>
            <div className="text-xs text-gray-500">
              {[value.designation, value.department].filter(Boolean).join(' · ')}
            </div>
            {value.email && <div className="text-xs text-gray-400">{value.email}</div>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => { onSelect(null); setQuery(''); }}
          className="text-gray-400 hover:text-gray-700"
          aria-label="Clear selection"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className={`${fc} pl-9`}
          value={query}
          placeholder="Search the employee master by name, email or ID…"
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {loading && <div className="px-3 py-2 text-xs text-gray-400">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">No matching employees.</div>
          )}
          {results.map(b => (
            <button
              key={b.id}
              type="button"
              onClick={() => { onSelect(b); setOpen(false); }}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-violet-50"
            >
              <div>
                <div className="text-sm font-medium text-navy">{b.name}</div>
                <div className="text-xs text-gray-400">
                  {[b.designation, b.department, b.email].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span className="font-mono text-[10px] text-gray-400">{b.employeeId}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
